import React, { useMemo } from 'react';

import { Image } from 'antd';

import styled from 'styled-components';

import { isLandscape } from '@libs/NwUtils';

import NwIcon from '@components/Gui/NwIcon';

import { usePlaylistEditorDispatch } from './PlaylistEditorContext';
import { getPlaceholderPictureForBook } from './PlaylistEditorUtils';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';

const DraggableDivWrapper = styled.div`
    width: var(--item-width);
    height: auto;
    aspect-ratio: 3/4;
    background-color: ${(props) => props.theme.DarkerBorderColor};
    /* box-shadow:
        0px 0px 0px rgba(3, 7, 18, 0.1),
        0px 1px 1px rgba(3, 7, 18, 0.08),
        0px 1px 1px rgba(3, 7, 18, 0.06),
        0px 3px 3px rgba(3, 7, 18, 0.04),
        0px 4px 4px rgba(3, 7, 18, 0.02); */

    color: #fff;
    font-size: 0.8rem;

    position: relative;

    &.landscape {
        aspect-ratio: 16/10.2;
        width: calc((var(--item-width) * 2) + 0.75rem);
    }

    &.placeholder {
        background-color: #000;
    }

    &.in-playlist {
        opacity: 0.25;
    }

    .ant-image {
        width: 100%;
        height: 100%;
        overflow: hidden;
        position: relative;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
        }
        .ant-image-mask {
            top: auto;
            left: auto;
            width: 100%;
            height: var(--item-preview-trigger-height);
            background-color: #000;
            opacity: 0.25;

            &:hover {
                opacity: 0.8;
                background-color: ${(props) => props.theme.PrimaryColor};
            }
        }
    }

    .draggable-video-preview {
        width: 100%;
        height: 100%;
        position: relative;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
        }

        .video-mask {
            position: absolute;
            top: auto;
            left: 0;
            bottom: 0;
            right: 0;

            display: flex;
            align-items: center;
            justify-content: center;
            gap: 1rem;

            width: 100%;
            height: var(--item-preview-trigger-height);

            opacity: 0.25;
            color: #fff;
            background: rgba(0, 0, 0, 0.5);
            transition: opacity 0.3s;

            cursor: pointer;

            &:hover {
                opacity: 0.8;
                background-color: ${(props) => props.theme.PrimaryColor};
            }
        }
    }

    .draggable-div-handler {
        background-color: transparent;
        position: absolute;
        top: 0;
        right: 0;
        left: 0;
        bottom: 2rem;
        cursor: move;
        z-index: 100;
    }

    .draggable-add-button {
        position: absolute;
        top: -0.5rem;
        right: -0.5rem;
        font-size: 1rem;
        background-color: ${(props) => props.theme.PrimaryColor};
        color: #fff;
        border-radius: 50%;
        width: 1.75rem;
        height: 1.75rem;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        z-index: 200;
    }

    &.dragging {
        box-shadow:
            0px 1px 1px rgba(3, 7, 18, 0.1),
            0px 5px 5px rgba(3, 7, 18, 0.08),
            0px 12px 12px rgba(3, 7, 18, 0.06),
            0px 20px 20px rgba(3, 7, 18, 0.04),
            0px 32px 32px rgba(3, 7, 18, 0.02);

        .draggable-add-button {
            opacity: 0;
        }

        .ant-image {
            .ant-image-mask {
                opacity: 0;
            }
        }
    }

    span {
        position: absolute;
        top: 0.5rem;
        left: 0.5rem;
    }
`;

const DraggableArchiveImage = ({ document, isInPlaylist, drag }) => {
    const dispatch = usePlaylistEditorDispatch();

    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: `draggable-${document ? document.ID : 'placeholder'}`,
        data: { document },
        disabled: isInPlaylist,
    });

    const style = transform
        ? {
              transform: CSS.Translate.toString(transform),
          }
        : undefined;

    const isPictureLandscape = document && isLandscape(document);
    const isDocumentPlaceholder = document === null;

    let imageUrl = '';
    let thumbnailUrl = '';

    if (!isDocumentPlaceholder) {
        imageUrl = document.HD ? document.HD.Url : document.Standard.Url;
        thumbnailUrl = document.Thumbnail ? document.Thumbnail.Url : '';
    }

    const handleAddItem = () => {
        let newDocumentToAdd = null;
        if (!isDocumentPlaceholder) {
            newDocumentToAdd = { ...document };
        } else {
            newDocumentToAdd = { ...getPlaceholderPictureForBook() };
        }
        dispatch({
            type: 'AddPlaylistItem',
            placeholder: isDocumentPlaceholder,
            value: {
                ID: newDocumentToAdd.ID,
                media: newDocumentToAdd,
            },
        });
    };

    const draggableDivClasses = useMemo(() => {
        let classes = 'draggable-div-item';
        if (isPictureLandscape) {
            classes += ' landscape';
        }
        if (isInPlaylist) {
            classes += ' in-playlist';
        }
        if (isDocumentPlaceholder) {
            classes += ' placeholder';
        }
        if (drag || isDragging) {
            classes += ' dragging';
        }
        return classes;
    }, [isPictureLandscape, isInPlaylist, isDocumentPlaceholder, drag, isDragging]);

    const showAddButton = useMemo(() => {
        return !isInPlaylist && !drag && !isDragging;
    }, [isInPlaylist, drag, isDragging]);

    const isVideo = document && document.Type === 'Video';

    return (
        <DraggableDivWrapper
            className={draggableDivClasses}
            key={document ? document.ID : 'placeholder'}
            ref={setNodeRef}
            style={style}
        >
            {!isInPlaylist && <div className="draggable-div-handler" {...listeners} {...attributes}></div>}
            {!isDocumentPlaceholder && (
                <>
                    {isVideo ? (
                        <div
                            onClick={() =>
                                dispatch({
                                    type: 'SetPreviewVideo',
                                    value: document,
                                })
                            }
                            className="draggable-video-preview"
                        >
                            <img src={thumbnailUrl} />
                            <div className="video-mask">
                                <NwIcon icon={byPrefixAndName.fass['play']} />
                                Preview
                            </div>
                        </div>
                    ) : (
                        <Image
                            src={thumbnailUrl}
                            preview={{
                                src: imageUrl,
                            }}
                        />
                    )}
                </>
            )}
            {showAddButton && (
                <div className="draggable-add-button" onClick={handleAddItem}>
                    <NwIcon icon={byPrefixAndName.fass['plus']} />
                </div>
            )}
        </DraggableDivWrapper>
    );
};

export default DraggableArchiveImage;
